import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { RadioCheckList } from 'ui-components/components/radio-check-list';
import { RadioCheckListItem } from 'ui-components/components/radio-check-list/radio-check-list-item';

import { createLookupHash } from '../../../../../../services/hash-service';
import { CANCEL_ACCOUNT_PAGE, STAMPS_CORE_PLAN_PRICE, STAMPS_CORE_PLAN_ID, DROPDOWN_QUESTION } from '../../../constants';
import { usePaymentAndSubscriptionPageContext } from '../../../context/payment-and-subscription-page-context';
import { messages } from '../../../messages';
import style from '../../../style.module.less';

const CancelStampsAccountContent = ({ onClose }) => {
  const {
    cancelReasonId,
    comments,
    followUpCancelReasonId,
    followUpExplain,
    cancellationInfo,
    setCurrentPage,
    sellerStampsProviders,
    cancelSubscriptionCall,
  } = usePaymentAndSubscriptionPageContext();

  const stampsProvidersArray = sellerStampsProviders.reduce((acc, account) => {
    acc.push({
      sellerProviderId: account.sellerLabelProviderId ?? '',
      requestedStampsBillingPlanId: STAMPS_CORE_PLAN_ID,
      shouldCancel: false,
      name: account.name,
    });
    return acc;
  }, []);

  const [sellerAccountsToCancelArray, setSellerAccountsToCancelArray] = useState(stampsProvidersArray);
  const [keepStampsAccountOpen, setKeepStampsAccountOpen] = useState(true);

  const onKeepStampsAccountsOpenSelected = useCallback(() => {
    setKeepStampsAccountOpen(true);
    const updatedAccounts = sellerAccountsToCancelArray.reduce((acc, account) => {
      acc.push({
        sellerProviderId: account.sellerProviderId ?? '',
        requestedStampsBillingPlanId: STAMPS_CORE_PLAN_ID,
        shouldCancel: false,
        name: account.name,
      });
      return acc;
    }, []);
    setSellerAccountsToCancelArray(updatedAccounts);
  }, [sellerAccountsToCancelArray, setKeepStampsAccountOpen]);

  const onCancellAllStampsAccountsOpenSelected = () => {
    setKeepStampsAccountOpen(false);
    const updatedAccounts = sellerAccountsToCancelArray.reduce((acc, account) => {
      acc.push({
        sellerProviderId: account.sellerProviderId ?? '',
        requestedStampsBillingPlanId: '',
        shouldCancel: true,
        name: account.name,
      });
      return acc;
    }, []);
    setSellerAccountsToCancelArray(updatedAccounts);
  };

  const onCheckboxChange = (sellerProviderId) => {
    const updatedAccounts = [...sellerAccountsToCancelArray];
    const indexOfAccountToUpdate = updatedAccounts.findIndex((provider) => provider.sellerProviderId === sellerProviderId);
    const { sellerProviderId: sellerProviderIdValue, shouldCancel, name } = updatedAccounts[indexOfAccountToUpdate];
    updatedAccounts.splice(indexOfAccountToUpdate, 1, {
      sellerProviderId: sellerProviderIdValue,
      requestedStampsBillingPlanId: shouldCancel ? STAMPS_CORE_PLAN_ID : '',
      shouldCancel: !shouldCancel,
      name,
    });
    setSellerAccountsToCancelArray(updatedAccounts);
  };

  const { cancelReasons = [] } = cancellationInfo;
  const cancelReasonData = createLookupHash(cancelReasons, 'cancelReasonId');

  const onSave = useCallback(() => {
    const { cancelReasonText, cancelFollowUpQuestions } = cancelReasonData[cancelReasonId];
    const { cancelSubReasons, answerTypeId } = cancelFollowUpQuestions[0].cancelSubReasons;
    const followUpReasonText =
      answerTypeId === DROPDOWN_QUESTION
        ? (cancelSubReasons.find((sr) => sr.cancelSubReasonId === followUpCancelReasonId).followupReasonText ?? '')
        : followUpExplain;
    const followUpReasonId = parseInt(followUpCancelReasonId, 10) === 0 ? null : followUpCancelReasonId;
    const payload = {
      cancelReasonId,
      cancelReasonText,
      followUpReasonId,
      followUpReasonText,
      explanation: comments,
      hostUrl: window.location.host,
      stampsAccountToConvertOrCancel: sellerAccountsToCancelArray,
    };

    cancelSubscriptionCall(payload);
  }, [
    cancelReasonData,
    cancelReasonId,
    followUpExplain,
    followUpCancelReasonId,
    comments,
    sellerAccountsToCancelArray,
    cancelSubscriptionCall,
  ]);

  const onCloseModal = useCallback(() => {
    setCurrentPage(CANCEL_ACCOUNT_PAGE);
    onKeepStampsAccountsOpenSelected();
    onClose();
  }, [onClose, setCurrentPage, onKeepStampsAccountsOpenSelected]);

  return (
    <Modal className={style['modal-base']} size='medium'>
      <ModalHeader closeModal={onCloseModal}>
        <FormattedMessage {...messages.cancelSubscriptionModalHeader} />
      </ModalHeader>
      <ModalBody className={style['cancellation-modal-body']}>
        <div className={style['cancellation-modal-description']}>
          <FormattedMessage {...messages.howToResolveStampsAccount} values={{ numberOfFreeStampsPlans: sellerStampsProviders.length }} />
        </div>
        <RadioCheckList>
          <RadioCheckListItem isChecked={keepStampsAccountOpen} onChecked={onKeepStampsAccountsOpenSelected}>
            <FormattedMessage
              {...messages.cancelAndKeepStampsAccountOpen}
              values={{
                stampsPrice: STAMPS_CORE_PLAN_PRICE,
                numberOfFreeStampsPlans: sellerStampsProviders.length,
              }}
            />
          </RadioCheckListItem>
          {sellerAccountsToCancelArray.length > 1 && (
            <div className={style['multiple-stamps-accounts-notice']}>
              <div className={style['choose-account-notice']}>
                <FormattedMessage {...messages.moreThanOneStampsAccountMessagen} values={{ stampsPrice: STAMPS_CORE_PLAN_PRICE }} />
              </div>
              {sellerAccountsToCancelArray
                .filter((a) => a)
                .map((account) => (
                  <div key={account.sellerProviderId} className={style['account-checkbox']}>
                    <Checkbox
                      key={account.sellerProviderId}
                      checked={!account.shouldCancel}
                      className={style['checkbox-height']}
                      disabled={!keepStampsAccountOpen}
                      label={
                        <FormattedMessage
                          {...messages.keepSpecificStampsAccountOpen}
                          values={{
                            b: (chunks) => <b>{chunks}</b>,
                            accountNickname: account.name,
                            stampsPrice: STAMPS_CORE_PLAN_PRICE,
                          }}
                        />
                      }
                      onChange={() => onCheckboxChange(account?.sellerProviderId)}
                    />
                  </div>
                ))}
            </div>
          )}
          <RadioCheckListItem isChecked={!keepStampsAccountOpen} onChecked={onCancellAllStampsAccountsOpenSelected}>
            <FormattedMessage {...messages.cancelPlanAndStampsAccount} values={{ numberOfFreeStampsPlans: sellerStampsProviders.length }} />
          </RadioCheckListItem>
        </RadioCheckList>
      </ModalBody>
      <ModalFooter className={style.footer}>
        <Button onClick={onCloseModal}>
          <FormattedMessage {...messages.cancel} />
        </Button>
        <Button onClick={onSave} theme='primary'>
          <FormattedMessage {...messages.cancelSubscription} />
        </Button>
      </ModalFooter>
    </Modal>
  );
};

CancelStampsAccountContent.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default CancelStampsAccountContent;
